import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { farmCalendarParts } from "@/lib/farmTime";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
    CalendarDays,
    CheckCircle2,
    Clock3,
    CircleOff,
    CircleDot,
    Download,
    Moon,
    NotebookTabs,
    ShieldCheck,
    Sun,
    TriangleAlert,
} from "lucide-react";

const STATUS_META = {
    present: { label: "Present", icon: CheckCircle2, symbol: "P", tone: "bg-emerald-50 border-emerald-200 text-emerald-800" },
    late: { label: "Late", icon: Clock3, symbol: "L", tone: "bg-amber-50 border-amber-200 text-amber-800" },
    absent: { label: "Absent", icon: CircleOff, symbol: "A", tone: "bg-rose-50 border-rose-200 text-rose-800" },
    leave: { label: "Leave", icon: NotebookTabs, symbol: "LV", tone: "bg-sky-50 border-sky-200 text-sky-800" },
    approved_leave: { label: "Approved Leave", icon: NotebookTabs, symbol: "AL", tone: "bg-cyan-50 border-cyan-200 text-cyan-800" },
    pending_leave: { label: "Pending Leave", icon: TriangleAlert, symbol: "PL", tone: "bg-orange-50 border-orange-200 text-orange-800" },
    half_day: { label: "Half Day", icon: Clock3, symbol: "HD", tone: "bg-blue-50 border-blue-200 text-blue-800" },
    rest_day: { label: "Rest Day", icon: Moon, symbol: "R", tone: "bg-slate-50 border-slate-200 text-slate-800" },
    holiday: { label: "Holiday", icon: Sun, symbol: "H", tone: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800" },
    pending: { label: "Pending", icon: TriangleAlert, symbol: "PD", tone: "bg-orange-50 border-orange-200 text-orange-800" },
    no_record: { label: "No Record", icon: CircleDot, symbol: "N", tone: "bg-zinc-50 border-zinc-200 text-zinc-700" },
};

function statusMeta(status) {
    return STATUS_META[status] || STATUS_META.no_record;
}

function monthName(month) {
    return new Date(2000, Number(month) - 1, 1).toLocaleString("en-PH", { month: "long" });
}

function dayNumber(iso) {
    return Number(String(iso || "").slice(8, 10) || 0);
}

function prettyHours(value) {
    return `${Number(value || 0).toFixed(2)}h`;
}

function num(v) {
    return Number(v || 0);
}

function formatDateLabel(iso) {
    if (!iso) return "-";
    const [y, m, d] = String(iso).split("-").map((x) => Number(x || 0));
    if (!y || !m || !d) return String(iso);
    const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return dt.toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric", weekday: "long", timeZone: "Asia/Manila" });
}

export default function AttendanceCalendarPage() {
    const { user, isOwner } = useAuth();
    const nav = useNavigate();
    const [params, setParams] = useSearchParams();
    const farmToday = farmCalendarParts();

    const ownerView = isOwner();
    const employeeId = ownerView ? (params.get("employee_id") || "") : "";

    const [year, setYear] = useState(Number(params.get("year") || farmToday.year));
    const [month, setMonth] = useState(Number(params.get("month") || farmToday.month));

    const [employee, setEmployee] = useState(null);
    const [calendarRows, setCalendarRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [settings, setSettings] = useState({});

    const [selectedDate, setSelectedDate] = useState("");
    const [dayDetail, setDayDetail] = useState(null);
    const [dayOpen, setDayOpen] = useState(false);

    const [loading, setLoading] = useState(false);

    const loadMonth = useCallback(async () => {
        setLoading(true);
        try {
            const url = ownerView
                ? `/attendance/employee/${encodeURIComponent(employeeId)}/calendar`
                : "/attendance/my-calendar";
            if (ownerView && !employeeId) {
                setEmployee(null);
                setCalendarRows([]);
                setSummary({});
                return;
            }
            const { data } = await api.get(url, { params: { year, month } });
            setEmployee(data?.employee || null);
            setCalendarRows(Array.isArray(data?.rows) ? data.rows : []);
            setSummary(data?.summary || {});
            setSettings(data?.settings || {});
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load attendance calendar");
            setCalendarRows([]);
            setSummary({});
            setEmployee(null);
        } finally {
            setLoading(false);
        }
    }, [ownerView, employeeId, year, month]);

    const openDay = useCallback(async (isoDate) => {
        try {
            const url = ownerView
                ? `/attendance/employee/${encodeURIComponent(employeeId)}/day/${encodeURIComponent(isoDate)}`
                : `/attendance/my-day/${encodeURIComponent(isoDate)}`;
            const { data } = await api.get(url);
            setSelectedDate(isoDate);
            setDayDetail(data || null);
            setDayOpen(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load day details");
        }
    }, [ownerView, employeeId]);

    useEffect(() => {
        setParams((old) => {
            const next = new URLSearchParams(old);
            next.set("year", String(year));
            next.set("month", String(month));
            if (ownerView) {
                if (employeeId) next.set("employee_id", employeeId);
            } else {
                next.delete("employee_id");
            }
            return next;
        });
    }, [year, month, ownerView, employeeId, setParams]);

    useEffect(() => {
        loadMonth();
    }, [loadMonth]);

    const rowsByDate = useMemo(() => {
        const map = new Map();
        for (const row of calendarRows || []) {
            map.set(row.date, row);
        }
        return map;
    }, [calendarRows]);

    const calendarCells = useMemo(() => {
        const first = new Date(year, month - 1, 1);
        const firstDayIndex = (first.getDay() + 6) % 7;
        const daysInMonth = new Date(year, month, 0).getDate();
        const cells = [];
        for (let i = 0; i < firstDayIndex; i += 1) cells.push({ blank: true, key: `blank-${i}` });
        for (let d = 1; d <= daysInMonth; d += 1) {
            const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
            cells.push({
                key: iso,
                date: iso,
                day: d,
                row: rowsByDate.get(iso) || { date: iso, primary_status: "no_record", badges: [] },
            });
        }
        return cells;
    }, [year, month, rowsByDate]);

    const runOwnerAttendanceAction = async (action) => {
        const attendanceId = dayDetail?.owner_controls?.attendance_id;
        if (!attendanceId) return;
        try {
            if (action === "approve") {
                await api.put(`/attendance/${attendanceId}/approve`, { note: "Approved from calendar" });
            } else if (action === "reject") {
                const reason = window.prompt("Reject reason");
                if (!reason) return;
                await api.put(`/attendance/${attendanceId}/reject`, { reason });
            } else if (action === "adjust") {
                const reason = window.prompt("Adjustment reason");
                if (!reason) return;
                await api.put(`/attendance/${attendanceId}/adjust`, { reason });
            } else if (action === "lock") {
                const reason = window.prompt("Lock reason (optional)") || "";
                await api.put(`/attendance/${attendanceId}/lock`, { reason });
            }
            toast.success(`Attendance ${action}d`);
            await Promise.all([loadMonth(), openDay(selectedDate)]);
        } catch (err) {
            toast.error(err.response?.data?.detail || `Unable to ${action} attendance`);
        }
    };

    const exportSummaryPdf = () => {
        const doc = new jsPDF();
        doc.setFontSize(14);
        doc.text("Employee Attendance Monthly Summary", 14, 16);
        doc.setFontSize(10);
        doc.text(`Employee: ${employee?.name || "-"} (${employee?.employee_code || "-"})`, 14, 24);
        doc.text(`Department: ${employee?.department || "-"} | Assigned Area: ${employee?.assigned_area || "-"}`, 14, 30);
        doc.text(`Period: ${monthName(month)} ${year} | Timezone: Asia/Manila (Philippine Time)`, 14, 36);

        const body = [
            ["Scheduled Working Days", summary.scheduled_working_days || 0],
            ["Present Days", summary.present_days || 0],
            ["Late Days", summary.late_days || 0],
            ["Absent Days", summary.absent_days || 0],
            ["Leave Days", summary.leave_days || 0],
            ["Rest Days", summary.rest_days || 0],
            ["Regular Hours", prettyHours(summary.regular_hours)],
            ["Paid Break Hours", prettyHours(summary.paid_break_hours)],
            ["Unpaid Lunch Hours", prettyHours(summary.unpaid_lunch_hours)],
            ["Excess Break Hours", prettyHours(summary.excess_break_hours)],
            ["Approved Overtime Hours", prettyHours(summary.approved_overtime_hours)],
            ["Pending Overtime Hours", prettyHours(summary.pending_overtime_hours)],
            ["Approved Attendance Days", summary.approved_attendance_days || 0],
            ["Pending Attendance Days", summary.pending_attendance_days || 0],
        ];

        autoTable(doc, {
            startY: 42,
            head: [["Metric", "Value"]],
            body,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [46, 125, 50] },
        });
 doc.save(
  `attendance-summary-${employee?.employee_code || employee?.id || "employee"}-${year}-${String(month).padStart(2, "0")}.pdf`
);
    };

    const exportCalendarPdf = () => {
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Employee Attendance Calendar", 14, 16);
        doc.setFontSize(10);
        doc.text(`Employee: ${employee?.name || "-"} (${employee?.employee_code || "-"})`, 14, 24);
        doc.text(`Month: ${monthName(month)} ${year} | Asia/Manila (Philippine Time)`, 14, 30);

        const body = (calendarRows || []).map((row) => [
            row.date,
            statusMeta(row.primary_status).label,
            (row.badges || []).join(", ") || "-",
            row.clock_in || "-",
            row.clock_out || "-",
            prettyHours(row.approved_overtime_hours || 0),
            prettyHours(row.pending_overtime_hours || 0),
        ]);

        autoTable(doc, {
            startY: 36,
            head: [["Date", "Primary Status", "Badges", "Clock In", "Clock Out", "Approved OT", "Pending OT"]],
            body,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [46, 125, 50] },
        });
        doc.save(`attendance-calendar-${employee?.employee_code || employee?.id || "employee"}-${year}-${String(month).padStart(2, "0")}.pdf`);
    };

    const infoSummary = [
        ["Present Days", summary.present_days],
        ["Late Days", summary.late_days],
        ["Absent Days", summary.absent_days],
        ["Leave Days", summary.leave_days],
        ["Rest Days", summary.rest_days],
        ["Regular Hours", prettyHours(summary.regular_hours)],
        ["Approved Overtime Hours", prettyHours(summary.approved_overtime_hours)],
        ["Pending Approval Days", summary.pending_attendance_days || 0],
    ];

    return (
        <div className="space-y-4" data-testid="attendance-calendar-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{ownerView ? "Owner View" : "Profile"}</div>
                    <h1 className="text-2xl font-bold tracking-tight">{i18n.t("batch1.0ac1f92dafde")}</h1>
                    <p className="text-sm text-muted-foreground">{i18n.t("batch1.dc7ac0ea52d3")}</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" className="h-10" onClick={exportSummaryPdf}><Download className="w-4 h-4 mr-2" />{i18n.t("batch1.a0547a8e2033")}</Button>
                    <Button variant="outline" className="h-10" onClick={exportCalendarPdf}><CalendarDays className="w-4 h-4 mr-2" />{i18n.t("batch1.746fbf1fe9ce")}</Button>
                    {ownerView ? <Button variant="outline" className="h-10" onClick={() => nav("/attendance")}>{i18n.t("batch1.484745909404")}</Button> : null}
                </div>
            </div>

            <Card className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                    <div>
                        <Label className="tiny-label">{i18n.t("batch1.079711ea16f3")}</Label>
                        <div className="mt-2 text-sm font-medium">{employee?.name || user?.name || "-"}</div>
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch1.c140767afa8d")}</Label>
                        <div className="mt-2 text-sm font-medium">{employee?.employee_code || "-"}</div>
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch1.db40106a4051")}</Label>
                        <div className="mt-2 text-sm font-medium">{employee?.department || "-"}</div>
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch1.a65de8bce998")}</Label>
                        <div className="mt-2 text-sm font-medium">{employee?.assigned_area || "-"}</div>
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch1.879e32326c52")}</Label>
                        <Input type="number" className="mt-2 h-10" value={year} onChange={(e) => setYear(Number(e.target.value || farmToday.year))} />
                    </div>
                    <div>
                        <Label className="tiny-label">{i18n.t("batch1.082bc378cd60")}</Label>
                        <Input type="number" min="1" max="12" className="mt-2 h-10" value={month} onChange={(e) => setMonth(Number(e.target.value || 1))} />
                    </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm text-muted-foreground">{i18n.t("batch1.4b4cbf19953a")} {monthName(month)} {year}</div>
                    <Button className="h-10" onClick={loadMonth} disabled={loading}>{loading ? "Loading..." : "Refresh"}</Button>
                </div>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {infoSummary.map(([label, value]) => (
                    <Card key={label} className="p-3">
                        <div className="tiny-label">{label}</div>
                        <div className="text-xl font-semibold mt-1">{value || 0}</div>
                    </Card>
                ))}
            </div>

            <Card className="p-4 overflow-hidden" data-testid="attendance-calendar-grid">
                <div className="mb-3 flex flex-wrap gap-2 text-xs">
                    {[
                        "present",
                        "late",
                        "absent",
                        "leave",
                        "approved_leave",
                        "pending_leave",
                        "half_day",
                        "rest_day",
                        "overtime",
                        "pending_approval",
                        "approved",
                    ].map((key) => {
                        if (key === "overtime") {
                            return <LegendPill key={key} label={i18n.t("batch1.b758d32ce6c0")} symbol="OT" tone="bg-indigo-50 border-indigo-200 text-indigo-800" />;
                        }
                        if (key === "pending_approval") {
                            return <LegendPill key={key} label={i18n.t("batch1.7ba0a4f030d1")} symbol="PD" tone="bg-orange-50 border-orange-200 text-orange-800" />;
                        }
                        if (key === "approved") {
                            return <LegendPill key={key} label={i18n.t("batch1.41b81eb8db1b")} symbol="AP" tone="bg-emerald-50 border-emerald-200 text-emerald-800" />;
                        }
                        const meta = statusMeta(key);
                        return <LegendPill key={key} label={meta.label} symbol={meta.symbol} tone={meta.tone} />;
                    })}
                </div>

                <div className="grid grid-cols-7 gap-2 text-xs font-semibold text-muted-foreground mb-2">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-center">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-2">
                    {calendarCells.map((cell) => {
                        if (cell.blank) return <div key={cell.key} className="min-h-[72px]" />;
                        const meta = statusMeta(cell.row.primary_status);
                        const Icon = meta.icon;
                        const badges = Array.isArray(cell.row.badges) ? cell.row.badges : [];
                        return (
                            <button
                                type="button"
                                key={cell.key}
                                className={`min-h-[86px] rounded-lg border p-2 text-left ${meta.tone} hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary`}
                                onClick={() => openDay(cell.date)}
                                data-testid={`attendance-day-${cell.date}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-bold">{cell.day}</span>
                                    <Icon className="w-3.5 h-3.5" />
                                </div>
                                <div className="mt-1 text-[11px] font-medium leading-tight">{meta.label}</div>
                                <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                                    {badges.includes("overtime") ? <span className="px-1 rounded border border-indigo-300">{i18n.t("batch1.b5441c89188d")}</span> : null}
                                    {badges.includes("pending_approval") ? <span className="px-1 rounded border border-orange-300">{i18n.t("batch1.67933b628589")}</span> : null}
                                    {badges.includes("approved") ? <span className="px-1 rounded border border-emerald-300">{i18n.t("batch1.bc5af774c819")}</span> : null}
                                    {badges.includes("manual_override") ? <span className="px-1 rounded border border-violet-300">{i18n.t("batch1.6ecc5900b43c")}</span> : null}
                                    {badges.includes("adjusted") ? <span className="px-1 rounded border border-cyan-300">{i18n.t("batch1.da450df06c38")}</span> : null}
                                    {badges.includes("locked") ? <span className="px-1 rounded border border-slate-400">{i18n.t("batch1.e3e63f75b4ce")}</span> : null}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </Card>

            <Dialog open={dayOpen} onOpenChange={setDayOpen}>
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.t("batch1.1a043363d06f")} {formatDateLabel(selectedDate)}</DialogTitle>
                        <DialogDescription>{i18n.t("batch1.2c32db022782")}</DialogDescription>
                    </DialogHeader>

                    {dayDetail ? (
                        <div className="space-y-4 text-sm">
                            <Card className="p-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <Field label={i18n.t("batch1.079711ea16f3")} value={dayDetail.employee?.name} />
                                    <Field label={i18n.t("batch1.23bd7dfc67c2")} value={dayDetail.employee?.id} />
                                    <Field label={i18n.t("batch1.db40106a4051")} value={dayDetail.employee?.department} />
                                    <Field label={i18n.t("batch1.1b60380406cd")} value={dayDetail.employee?.assigned_area} />
                                    <Field label={i18n.t("batch1.eb9a4bc1c0c1")} value={dayDetail.date} />
                                    <Field label={i18n.t("batch1.992e832bf95c")} value={dayDetail.scheduled_shift?.label} />
                                    <Field label={i18n.t("batch1.ef4485a7d9ed")} value="Asia/Manila (Philippine Time)" />
                                    <Field label={i18n.t("batch1.43ff2f873398")} value={dayDetail.attendance_status} />
                                    <Field label={i18n.t("batch1.a369b490afc9")} value={dayDetail.approval_status} />
                                </div>
                            </Card>

                            <Card className="p-3">
                                <h3 className="font-semibold mb-2">{i18n.t("batch1.dc21d2b4eede")}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Field label={i18n.t("batch1.c36f953ce513")} value={dayDetail.arrival?.shift_start} />
                                    <Field label={i18n.t("batch1.0cf1f544b8e0")} value={String(dayDetail.arrival?.gps || "-")} />
                                    <Field label={i18n.t("batch1.a5a74a6df092")} value={dayDetail.arrival?.device} />
                                    <Field label={i18n.t("batch1.4039d9ab8154")} value={dayDetail.arrival?.photo_verification} />
                                </div>
                            </Card>

                            <Card className="p-3">
                                <h3 className="font-semibold mb-2">{i18n.t("batch1.00040bab8a78")}</h3>
                                <Field label={i18n.t("batch1.8d3e921408c8")} value={dayDetail.work?.clock_in} />
                            </Card>

                            <Card className="p-3">
                                <h3 className="font-semibold mb-2">{i18n.t("batch1.50f88da05c8e")}</h3>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                                    <BreakCard title={i18n.t("batch1.6e1ffb50d629")} data={dayDetail.morning_break} paidLabel="Paid status" />
                                    <BreakCard title={i18n.t("batch1.585ecb76103a")} data={dayDetail.lunch} paidLabel="Unpaid status" />
                                    <BreakCard title={i18n.t("batch1.ac1482c53106")} data={dayDetail.afternoon_break} paidLabel="Paid status" />
                                </div>
                            </Card>

                            <Card className="p-3">
                                <h3 className="font-semibold mb-2">{i18n.t("batch1.b74bdee9c34f")}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Field label={i18n.t("batch1.7f9bf082bf6a")} value={dayDetail.finish?.clock_out} />
                                    <Field label={i18n.t("batch1.b4ebd03d7de2")} value={dayDetail.finish?.shift_end} />
                                </div>
                            </Card>

                            <Card className="p-3">
                                <h3 className="font-semibold mb-2">{i18n.t("batch1.c70d7c3ad58f")}</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    <Field label={i18n.t("batch1.3ccdbf4d7f99")} value={prettyHours(dayDetail.time_summary?.total_on_farm_hours)} />
                                    <Field label={i18n.t("batch1.8ba5bf47df7d")} value={prettyHours(dayDetail.time_summary?.regular_worked_hours)} />
                                    <Field label={i18n.t("batch1.c3b31b4e5d10")} value={prettyHours(dayDetail.time_summary?.paid_break_hours)} />
                                    <Field label={i18n.t("batch1.c89f591ea418")} value={prettyHours(dayDetail.time_summary?.unpaid_lunch_hours)} />
                                    <Field label={i18n.t("batch1.69152ef3561d")} value={prettyHours(dayDetail.time_summary?.excess_break_hours)} />
                                    <Field label={i18n.t("batch1.327b7992b55e")} value={String(dayDetail.time_summary?.late_minutes || 0)} />
                                    <Field label={i18n.t("batch1.0999bb9a8a81")} value={String(dayDetail.time_summary?.early_leave_minutes || 0)} />
                                    <Field label={i18n.t("batch1.8305cdfad2ff")} value={prettyHours(dayDetail.time_summary?.approved_overtime_hours)} />
                                    <Field label={i18n.t("batch1.8c73ae6a9e83")} value={prettyHours(dayDetail.time_summary?.pending_overtime_hours)} />
                                </div>
                            </Card>

                            <Card className="p-3">
                                <h3 className="font-semibold mb-2">{i18n.t("batch1.b758d32ce6c0")}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Field label={i18n.t("batch1.70123096c2b1")} value={dayDetail.overtime?.status} />
                                    <Field label={i18n.t("batch1.f219cc0614ae")} value={dayDetail.overtime?.reason} />
                                    <Field label={i18n.t("batch1.7b49076441ca")} value={dayDetail.overtime?.overtime_start} />
                                    <Field label={i18n.t("batch1.3800d43722bf")} value={dayDetail.overtime?.overtime_end} />
                                    <Field label={i18n.t("batch1.31d4ba32a2e0")} value={prettyHours(dayDetail.overtime?.requested_hours)} />
                                    <Field label={i18n.t("batch1.a4b3b69b782a")} value={prettyHours(dayDetail.overtime?.approved_hours)} />
                                    <Field label={i18n.t("batch1.2af30b2cf622")} value={dayDetail.overtime?.approval_notes} />
                                </div>
                            </Card>

                            <Card className="p-3">
                                <h3 className="font-semibold mb-2">{i18n.t("batch1.24ce812e7163")}</h3>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Field label={i18n.t("batch1.2a206d915208")} value={String(dayDetail.work_completed?.completed_field_mode_tasks || 0)} />
                                    <Field label={i18n.t("batch1.33bbad56533f")} value={String(dayDetail.work_completed?.feeding_records || 0)} />
                                    <Field label={i18n.t("batch1.fca4ca652254")} value={String(dayDetail.work_completed?.health_inspections || 0)} />
                                    <Field label={i18n.t("batch1.da5a77213322")} value={String(dayDetail.work_completed?.cleaning_records || 0)} />
                                    <Field label={i18n.t("batch1.5e629b9e6116")} value={String(dayDetail.work_completed?.machinery_inspections || 0)} />
                                    <Field label={i18n.t("batch1.8921cf0fbd0d")} value={String(dayDetail.work_completed?.incident_reports || 0)} />
                                    <Field label={i18n.t("batch1.b02b98e68182")} value={String(dayDetail.work_completed?.photo_count || 0)} />
                                    <Field label={i18n.t("batch1.b75eeb06c71c")} value={dayDetail.work_completed?.end_of_day_notes} />
                                </div>
                            </Card>

                            {ownerView && dayDetail.owner_controls?.attendance_id ? (
                                <Card className="p-3">
                                    <h3 className="font-semibold mb-2">{i18n.t("batch1.6b74bc75753a")}</h3>
                                    <div className="flex flex-wrap gap-2">
                                        <Button size="sm" onClick={() => runOwnerAttendanceAction("approve")}><ShieldCheck className="w-4 h-4 mr-1" />{i18n.t("batch1.7b2c7f146aba")}</Button>
                                        <Button size="sm" variant="outline" onClick={() => runOwnerAttendanceAction("reject")}>{i18n.t("batch1.2b03b59293b6")}</Button>
                                        <Button size="sm" variant="outline" onClick={() => runOwnerAttendanceAction("adjust")}>{i18n.t("batch1.ab166bd5e8c5")}</Button>
                                        <Button size="sm" variant="outline" onClick={() => runOwnerAttendanceAction("lock")}>{i18n.t("batch1.891ebccd5baa")}</Button>
                                    </div>
                                </Card>
                            ) : null}
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div className="rounded-lg border p-2">
            <div className="tiny-label">{label}</div>
            <div className="mt-1 break-words">{value || "-"}</div>
        </div>
    );
}

function BreakCard({ title, data, paidLabel }) {
    return (
        <div className="rounded-lg border p-2">
            <div className="font-medium mb-2">{title}</div>
            <div className="space-y-1 text-xs">
                <div>{i18n.t("batch1.1c7c77b27543")} <b>{data?.start || "-"}</b></div>
                <div>{i18n.t("batch1.b8ce37a1915f")} <b>{data?.end || "-"}</b></div>
                <div>{i18n.t("batch1.9693aeaaf68e")} <b>{Number(data?.duration_minutes || 0).toFixed(0)} {i18n.t("batch1.b6c935d4f3c7")}</b></div>
                <div>{paidLabel}: <b>{data?.is_paid ? "Paid" : "Unpaid"}</b></div>
                <div>{i18n.t("batch1.debd5dae9f6b")} <b>{Number(data?.allowed_minutes || 0)} {i18n.t("batch1.b6c935d4f3c7")}</b></div>
                <div>{i18n.t("batch1.c2f8da1d24cf")} <b>{Number(data?.excess_minutes || 0).toFixed(0)} {i18n.t("batch1.b6c935d4f3c7")}</b></div>
            </div>
        </div>
    );
}

function LegendPill({ label, symbol, tone }) {
    return (
        <span className={`inline-flex items-center gap-1 rounded border px-2 py-1 ${tone}`}>
            <b>{symbol}</b>
            <span>{label}</span>
        </span>
    );
}
